import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FavoriteRepositoryImpl } from "../../data/repository/favorite_repository_impl";
import { ResourceInterface } from "../interface/resource_interface";
import { FavoriteResourceInterface } from "../interface/favorite_resource_interface";

export interface ToggleResourceFavoriteVars {
  resourceId: string;
  userId: string;
  _favoriteId: string | null;
  next: boolean;
}

export interface ToggleResourceFavoriteResult {
  resourceId: string;
  _favoriteId: string | null;
}

export const useToggleResourceFavorite = () => {
  const repository = new FavoriteRepositoryImpl();
  const queryClient = useQueryClient();

  return useMutation<ToggleResourceFavoriteResult, Error, ToggleResourceFavoriteVars>({
    mutationFn: async ({ resourceId, userId, _favoriteId, next }) => {
      if (next) {
        const created = await repository.addFavorite(userId, resourceId);
        return { resourceId, _favoriteId: created._id };
      }
      if (!_favoriteId) {
        throw new Error("favoriteId requis pour retirer un favori");
      }
      await repository.removeFavorite(_favoriteId);
      return { resourceId, _favoriteId: null };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ResourceInterface | undefined>(
        ["resourceById", data.resourceId],
        (old) => old
          ? { ...old, _favoriteId: data._favoriteId }
          : old
      );

      queryClient.setQueriesData<FavoriteResourceInterface[] | undefined>(
        { queryKey: ["favoritesByUserIdAndThemeId", variables.userId], exact: false },
        (old) => {
          if (!old) return old;
          if (data._favoriteId) {
            if (old.some((f) => f._resourceId?._id === data.resourceId)) return old;
            const cached = queryClient.getQueryData<ResourceInterface>([
              "resourceById",
              data.resourceId,
            ]);
            if (!cached) return old;
            const now = new Date().toISOString();
            const entry: FavoriteResourceInterface = {
              _id: data._favoriteId,
              _userId: variables.userId,
              _resourceId: cached,
              _themeIds: cached._themeIds ?? [],
              createdAt: now,
              updatedAt: now,
              viewed: cached.viewed,
            };
            return [...old, entry];
          }
          return old.filter((f) => f._resourceId?._id !== data.resourceId);
        }
      );
    },
  });
};
